import paths from '@app/src/wf-constants/paths'
import { CheckOutlined, ErrorOutline, NotificationsOutlined } from '@mui/icons-material'
import { Box, Button, Divider, Stack, Typography, alpha, useTheme } from '@mui/material'
import React from 'react'
import { useIntl } from 'react-intl'
import { generatePath, useHistory } from 'react-router-dom'

type ImportDoneViewProps = {
  successCount: number
  errorCount: number
  onClose: () => void
}

const ImportDoneView: React.FC<ImportDoneViewProps> = ({ successCount, errorCount, onClose }) => {
  const { formatMessage } = useIntl()
  const { palette } = useTheme()
  const history = useHistory()

  const handleGoToNotifications = () => {
    history.push(generatePath(paths.notifications))
    onClose()
  }

  const hasSuccessfulImports = successCount > 0

  return (
    <Stack height="calc(100vh - 120px)" alignItems="center" justifyContent="center">
      <Stack alignItems="center" justifyContent="center" width={588}>
        {hasSuccessfulImports ? (
          <>
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                bgcolor: 'brandDecorative.emerald',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <CheckOutlined sx={{ color: 'common.white', fontSize: 40 }} />
            </Box>

            <Typography variant="h6" mt={3}>
              {formatMessage({ id: 'import.done.title' })}
            </Typography>

            <Typography color="textSecondary" mt={2} textAlign="center">
              {formatMessage({ id: 'import.done.description' }, { count: successCount })}
            </Typography>
          </>
        ) : (
          <>
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                bgcolor: 'error.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <ErrorOutline sx={{ color: 'common.white', fontSize: 40 }} />
            </Box>

            <Typography variant="h6" mt={3}>
              {formatMessage({ id: 'import.done.failedTitle' })}
            </Typography>

            <Typography color="textSecondary" mt={2} textAlign="center">
              {formatMessage({ id: 'import.done.failedDescription' })}
            </Typography>
          </>
        )}

        {errorCount > 0 && (
          <Box
            sx={{
              mt: 4,
              width: '100%',
              bgcolor: alpha(palette.error.main, 0.2),
              p: 2,
            }}
          >
            <Stack direction="row" spacing={2} alignItems="flex-start">
              <ErrorOutline sx={{ color: 'semantic.error' }} />
              <Stack flexGrow={1}>
                <Typography variant="subtitle1">
                  {formatMessage({ id: 'import.summary.failedToImport' }, { count: errorCount })}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {formatMessage({ id: 'import.done.errorDescription' })}
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Box display="flex" justifyContent="flex-end">
                  <Button
                    variant="text"
                    startIcon={<NotificationsOutlined />}
                    onClick={handleGoToNotifications}
                    sx={{ color: 'text.primary' }}
                  >
                    {formatMessage({ id: 'import.done.goToNotifications' })}
                  </Button>
                </Box>
              </Stack>
            </Stack>
          </Box>
        )}

        <Button variant="contained" sx={{ mt: 4 }} onClick={onClose}>
          {formatMessage({ id: 'general.close' })}
        </Button>
      </Stack>
    </Stack>
  )
}

export default ImportDoneView
